import CuttingPlaneInfo, { CuttingPlaneInfoStatus } from "./CuttingPlaneInfo";

enum AxisIndex {
  X = 0,
  Y = 1,
  Z = 2,
  FACE = 3,
}

type PlaneAxis = "x" | "y" | "z" | "face";

function getAxisIndex(planeAxis: PlaneAxis) {
  switch (planeAxis) {
    case "x":
      return AxisIndex.X;
    case "y":
      return AxisIndex.Y;
    case "z":
      return AxisIndex.Z;
    case "face":
      return AxisIndex.FACE;
    default:
      return null;
  }
}

class CuttingPlaneController {
  private _cuttingSections: Array<Communicator.CuttingSection>;

  private _modelBounding: Communicator.Box;

  private _planeInfo: Record<AxisIndex, CuttingPlaneInfo | undefined>;

  private _cadViewActivated: boolean;

  private _showReferenceGeometry: boolean;

  private _useIndividualCuttingSections: boolean;

  private _boundingBoxUpdate: boolean;

  private _faceSelection: Communicator.Selection.SelectionItem | null;

  private _modelStructureReady: boolean;

  private _pendingFunctions: { inverted?: () => void; visibility?: () => void };

  private _viewer: Communicator.WebViewer;

  constructor(viewer: Communicator.WebViewer) {
    this._cuttingSections = [];
    this._modelBounding = new Communicator.Box();
    this._planeInfo = {
      [AxisIndex.X]: undefined,
      [AxisIndex.Y]: undefined,
      [AxisIndex.Z]: undefined,
      [AxisIndex.FACE]: undefined,
    };
    this._cadViewActivated = false;
    this._showReferenceGeometry = true;
    this._useIndividualCuttingSections = true;
    this._boundingBoxUpdate = false;
    this._faceSelection = null;
    this._modelStructureReady = false;
    this._pendingFunctions = {};
    this._viewer = viewer;
    this.resetCuttingPlanes();
    this.setupCallbacks();
  }

  private setupCallbacks() {
    this._viewer.setCallbacks({
      modelStructureReady: () => {
        const promises: Promise<void>[] = [];
        promises.push(this._initSection());
        this._modelStructureReady = true;
        promises.push(this._updateBoundingBox());
        return Promise.all(promises);
      },
      partsVisibilityHidden: () => {
        this._updateBoundingBox();
      },
      partsVisibilityShown: () => {
        this._updateBoundingBox();
      },
      hwfParseComplete: () => {
        this._updateBoundingBox();
      },
      _modelSwitched: () => {
        return this._updateBoundingBox().then(() => this.resetCuttingPlanes());
      },
      _resetAssemblyTreeBegin: () => {
        return this._clearCuttingSections();
      },
    });
  }

  private _ensurePlaneInfo(axisIndex: AxisIndex) {
    let planeInfo = this._planeInfo[axisIndex];

    if (planeInfo === undefined) {
      planeInfo = new CuttingPlaneInfo();
      this._planeInfo[axisIndex] = planeInfo;
    }

    return planeInfo;
  }

  private _setPlaneInfoStatus(
    axisIndex: AxisIndex,
    status: CuttingPlaneInfoStatus
  ) {
    this._ensurePlaneInfo(axisIndex).status = status;
  }

  private _updateBoundingBox() {
    if (!this._boundingBoxUpdate && this._modelStructureReady) {
      this._boundingBoxUpdate = true;
      return this._viewer.model
        .getModelBounding(true, false)
        .then(modelBounding => {
          const minDiff = this._modelBounding.min.equalsWithTolerance(
            modelBounding.min,
            0.01
          );
          const maxDiff = this._modelBounding.max.equalsWithTolerance(
            modelBounding.max,
            0.01
          );
          let promise;
          if (!minDiff || !maxDiff) {
            this._modelBounding = modelBounding;
            this._ensurePlaneInfo(AxisIndex.X).updateReferenceGeometry = true;
            this._ensurePlaneInfo(AxisIndex.Y).updateReferenceGeometry = true;
            this._ensurePlaneInfo(AxisIndex.Z).updateReferenceGeometry = true;
            this._ensurePlaneInfo(AxisIndex.FACE).updateReferenceGeometry =
              true;
            const activeStates = {
              [AxisIndex.X]: this._isActive(AxisIndex.X),
              [AxisIndex.Y]: this._isActive(AxisIndex.Y),
              [AxisIndex.Z]: this._isActive(AxisIndex.Z),
              [AxisIndex.FACE]: this._isActive(AxisIndex.FACE),
            };
            this._storePlanes();
            promise = this._clearCuttingSections().then(() =>
              this._restorePlanes(activeStates)
            );
          } else {
            promise = Promise.resolve();
          }
          this._boundingBoxUpdate = false;
          return promise;
        });
    }

    return Promise.resolve();
  }

  private _resetAxis(axisIndex: AxisIndex) {
    delete this._planeInfo[axisIndex];

    if (axisIndex === AxisIndex.FACE) {
      this._faceSelection = null;
    }
  }

  public resetCuttingPlanes() {
    this._resetAxis(AxisIndex.X);
    this._resetAxis(AxisIndex.Y);
    this._resetAxis(AxisIndex.Z);
    this._resetAxis(AxisIndex.FACE);
    this._useIndividualCuttingSections = true;
    this._showReferenceGeometry = true;
    this._faceSelection = null;
    return this._clearCuttingSections();
  }

  private _initSection() {
    return this._viewer.model
      .getModelBounding(true, false)
      .then(modelBounding => {
        this._modelBounding = modelBounding.copy();

        const axisX = this._viewer.cuttingManager.getCuttingSection(
          AxisIndex.X
        );
        if (axisX) {
          this._cuttingSections[AxisIndex.X] = axisX;
        }

        const axisY = this._viewer.cuttingManager.getCuttingSection(
          AxisIndex.Y
        );
        if (axisY) {
          this._cuttingSections[AxisIndex.Y] = axisY;
        }

        const axisZ = this._viewer.cuttingManager.getCuttingSection(
          AxisIndex.Z
        );
        if (axisZ) {
          this._cuttingSections[AxisIndex.Z] = axisZ;
        }

        const axisFACE = this._viewer.cuttingManager.getCuttingSection(
          AxisIndex.FACE
        );
        if (axisFACE) {
          this._cuttingSections[AxisIndex.FACE] = axisFACE;
        }

        this._triggerPendingFuncs();
      });
  }

  private _triggerPendingFuncs() {
    if (this._pendingFunctions.inverted) {
      this._pendingFunctions.inverted();
      delete this._pendingFunctions.inverted;
    }

    if (this._pendingFunctions.visibility) {
      this._pendingFunctions.visibility();
      delete this._pendingFunctions.visibility;
    }
  }

  toggle(planeAxis: PlaneAxis) {
    const axisIndex = getAxisIndex(planeAxis);

    if (axisIndex === null) {
      return Promise.resolve();
    }

    let faceSelection = false;

    if (axisIndex === AxisIndex.FACE) {
      const selectionItem = this._viewer.selectionManager.getLast();

      if (selectionItem !== null && selectionItem.isFaceSelection()) {
        faceSelection = true;
        this._faceSelection = selectionItem;
      } else {
        faceSelection = false;
      }
    }

    const promises: Promise<void>[] = [];

    switch (this._ensurePlaneInfo(axisIndex).status) {
      case CuttingPlaneInfoStatus.HIDDEN:
        if (axisIndex === AxisIndex.FACE) {
          if (faceSelection) {
            // clear any cutting planes in the face cutting section
            promises.push(
              this._cuttingSections[axisIndex].clear().then(() => {
                this._cadViewActivated = false;
                this._setPlaneInfoStatus(
                  axisIndex,
                  CuttingPlaneInfoStatus.VISIBLE
                );
                return this.setCuttingPlaneVisibility(true, axisIndex);
              })
            );
          }
        } else {
          this._setPlaneInfoStatus(axisIndex, CuttingPlaneInfoStatus.VISIBLE);
          promises.push(this.setCuttingPlaneVisibility(true, axisIndex));
        }
        break;
      case CuttingPlaneInfoStatus.VISIBLE:
        this._setPlaneInfoStatus(axisIndex, CuttingPlaneInfoStatus.INVERTED);
        promises.push(this.setCuttingPlaneInverted(axisIndex));
        break;
      case CuttingPlaneInfoStatus.INVERTED:
        this._setPlaneInfoStatus(axisIndex, CuttingPlaneInfoStatus.HIDDEN);
        promises.push(this.setCuttingPlaneVisibility(false, axisIndex));
        break;
      default:
        break;
    }
    return Promise.all(promises);
  }

  getCount() {
    let count = 0;

    for (let i = 0; i < this._cuttingSections.length; i += 1) {
      count += this._cuttingSections[i].getCount();
    }

    return count;
  }

  setCuttingPlaneVisibility(visibility: boolean, axisIndex: AxisIndex) {
    if (!this._cuttingSections[this._getCuttingSectionIndex(axisIndex)]) {
      this._pendingFunctions.visibility = () => {
        this.setCuttingPlaneVisibility(visibility, axisIndex);
      };
      return Promise.resolve();
    }

    this._viewer.delayCapping();

    let promise: Promise<any>;

    if (visibility) {
      const planeInfo = this._ensurePlaneInfo(axisIndex);

      if (planeInfo.plane === null) {
        planeInfo.plane = this._generateCuttingPlane(axisIndex);
        planeInfo.referenceGeometry =
          this._generateReferenceGeometry(axisIndex);
      }

      promise = this._setSection(axisIndex);
    } else {
      promise = this.refreshPlaneGeometry();
    }

    const count = this.getCount();
    const active = this._isActive(axisIndex);

    return promise.then(() => {
      if (count > 0 && !active) {
        return this._activatePlanes();
      }

      if (active && count === 0) {
        return this._deactivateAxis(axisIndex);
      }

      return Promise.resolve();
    });
  }

  setCuttingPlaneInverted(axisIndex: AxisIndex) {
    if (!this._cuttingSections[this._getCuttingSectionIndex(axisIndex)]) {
      this._pendingFunctions.inverted = () => {
        this.setCuttingPlaneInverted(axisIndex);
      };

      return Promise.resolve();
    }

    this._viewer.delayCapping();

    const index = this._getPlaneIndex(axisIndex);
    const plane =
      this._cuttingSections[this._getCuttingSectionIndex(axisIndex)].getPlane(
        index
      );

    if (plane) {
      plane.normal.negate();
      plane.d *= -1;
      return this._cuttingSections[
        this._getCuttingSectionIndex(axisIndex)
      ].updatePlane(index, plane);
    }

    return Promise.resolve();
  }

  toggleReferenceGeometry() {
    this._showReferenceGeometry = !this._showReferenceGeometry;
    return this.refreshPlaneGeometry();
  }

  refreshPlaneGeometry() {
    this._storePlanes();
    return this._clearCuttingSections().then(() => this._restorePlanes());
  }

  toggleCuttingMode() {
    this._storePlanes();
    const promise = this._clearCuttingSections();
    this._useIndividualCuttingSections = !this._useIndividualCuttingSections;
    return promise.then(() => this._restorePlanes());
  }

  cadViewActivated() {
    this._cadViewActivated = true;
    this._setPlaneInfoStatus(AxisIndex.FACE, CuttingPlaneInfoStatus.HIDDEN);
  }

  private _isActive(axisIndex: AxisIndex) {
    return this._cuttingSections[
      this._getCuttingSectionIndex(axisIndex)
    ].isActive();
  }

  private _deactivateAxis(axisIndex: AxisIndex) {
    return this._cuttingSections[
      this._getCuttingSectionIndex(axisIndex)
    ].deactivate();
  }

  private _getCuttingSectionIndex(axis: AxisIndex) {
    return this._useIndividualCuttingSections ? axis : AxisIndex.X;
  }

  private _clearCuttingSection(axis) {
    if (this._cuttingSections[axis]) {
      return this._cuttingSections[axis].clear();
    }
    return Promise.resolve();
  }

  private _clearCuttingSections() {
    const promises: Array<Promise<void>> = [];
    promises.push(this._clearCuttingSection(AxisIndex.X));
    promises.push(this._clearCuttingSection(AxisIndex.Y));
    promises.push(this._clearCuttingSection(AxisIndex.Z));

    // don't clear out face section, it shares with CAD Views
    if (!this._cadViewActivated) {
      promises.push(this._clearCuttingSection(AxisIndex.FACE));
    }

    return Promise.all(promises) as unknown as Promise<void>;
  }

  private _activatePlane(axisIndex: AxisIndex) {
    const section = this._cuttingSections[axisIndex];

    if (section.getCount()) {
      return section.activate();
    }

    return Promise.resolve();
  }

  private async _activatePlanes(activeStates?: Record<AxisIndex, boolean>) {
    const promises: Promise<void>[] = [];

    if (!activeStates || activeStates[AxisIndex.X]) {
      promises.push(this._activatePlane(AxisIndex.X));
    }

    if (!activeStates || activeStates[AxisIndex.Y]) {
      promises.push(this._activatePlane(AxisIndex.Y));
    }

    if (!activeStates || activeStates[AxisIndex.Z]) {
      promises.push(this._activatePlane(AxisIndex.Z));
    }

    if (!activeStates || activeStates[AxisIndex.FACE]) {
      promises.push(this._activatePlane(AxisIndex.FACE));
    }

    await Promise.all(promises);
  }

  private _getPlaneIndex(axisIndex: AxisIndex) {
    if (this._useIndividualCuttingSections) {
      if (
        this._cuttingSections[this._getCuttingSectionIndex(axisIndex)].getPlane(
          0
        )
      ) {
        return 0;
      }
    } else {
      for (let i = 0; i < this._cuttingSections[0].getCount(); i += 1) {
        const plane = this._cuttingSections[0].getPlane(i);

        if (plane) {
          const normal = this._faceSelection?.getFaceEntity()?.getNormal();

          if (
            (plane.normal.x && axisIndex === AxisIndex.X) ||
            (plane.normal.y && axisIndex === AxisIndex.Y) ||
            (plane.normal.z && axisIndex === AxisIndex.Z) ||
            (axisIndex === AxisIndex.FACE &&
              normal &&
              plane.normal.equals(normal))
          ) {
            return i;
          }
        }
      }
    }

    return -1;
  }

  private _setSection(axisIndex: AxisIndex) {
    const planeInfo = this._planeInfo[axisIndex];

    if (planeInfo !== undefined && planeInfo.plane !== null) {
      const cuttingSection =
        this._cuttingSections[this._getCuttingSectionIndex(axisIndex)];
      const referenceGeometry = this._showReferenceGeometry
        ? planeInfo.referenceGeometry
        : undefined;
      return cuttingSection.addPlane(planeInfo.plane, referenceGeometry);
    }

    return Promise.resolve(false);
  }

  private _restorePlane(axisIndex: AxisIndex) {
    const planeInfo = this._planeInfo[axisIndex];

    if (
      planeInfo !== undefined &&
      planeInfo.plane !== null &&
      planeInfo.status !== CuttingPlaneInfoStatus.HIDDEN
    ) {
      if (
        planeInfo.referenceGeometry === null ||
        planeInfo.updateReferenceGeometry
      ) {
        planeInfo.referenceGeometry =
          this._generateReferenceGeometry(axisIndex);
      }

      return this._setSection(axisIndex);
    }

    return Promise.resolve(false);
  }

  private _restorePlanes(activeStates?: Record<AxisIndex, boolean>) {
    this._restorePlane(AxisIndex.X);
    this._restorePlane(AxisIndex.Y);
    this._restorePlane(AxisIndex.Z);
    this._restorePlane(AxisIndex.FACE);
    return this._activatePlanes(activeStates);
  }

  private _storePlane(axisIndex: AxisIndex) {
    const section =
      this._cuttingSections[this._getCuttingSectionIndex(axisIndex)];
    const planeInfo = this._ensurePlaneInfo(axisIndex);
    planeInfo.plane = null;
    planeInfo.referenceGeometry = null;

    if (
      section.getCount() > 0 &&
      planeInfo.status !== CuttingPlaneInfoStatus.HIDDEN
    ) {
      const planeIndex = this._getPlaneIndex(axisIndex);
      const plane = section.getPlane(planeIndex);
      const referenceGeometry = section.getReferenceGeometry(planeIndex);

      if (!(this._cadViewActivated && axisIndex === AxisIndex.FACE)) {
        planeInfo.plane = plane;
        planeInfo.referenceGeometry = referenceGeometry;
      }
    }
  }

  private _storePlanes() {
    this._storePlane(AxisIndex.X);
    this._storePlane(AxisIndex.Y);
    this._storePlane(AxisIndex.Z);
    this._storePlane(AxisIndex.FACE);
  }

  private _generateReferenceGeometry(axisIndex: AxisIndex) {
    let referenceGeometry: Communicator.Point3[] = [];
    let axis: Communicator.Axis | undefined;

    if (axisIndex === AxisIndex.FACE) {
      const normal = this._faceSelection?.getFaceEntity()?.getNormal();
      const position = this._faceSelection?.getPosition();

      if (normal && position) {
        referenceGeometry =
          this._viewer.cuttingManager.createReferenceGeometryFromFaceNormal(
            normal,
            position,
            this._modelBounding
          );
      }
    } else {
      switch (axisIndex) {
        case AxisIndex.X:
          axis = Communicator.Axis.X;
          break;
        case AxisIndex.Y:
          axis = Communicator.Axis.Y;
          break;
        case AxisIndex.Z:
          axis = Communicator.Axis.Z;
          break;
        default:
          break;
      }

      if (axis !== undefined) {
        referenceGeometry =
          this._viewer.cuttingManager.createReferenceGeometryFromAxis(
            axis,
            this._modelBounding
          );
      }
    }
    return referenceGeometry;
  }

  private _generateCuttingPlane(axisIndex: AxisIndex) {
    const plane = new Communicator.Plane();
    let normal: Communicator.Point3 | null | undefined;
    let position: Communicator.Point3 | null | undefined;

    switch (axisIndex) {
      case AxisIndex.X:
        plane.normal.set(1, 0, 0);
        plane.d = -this._modelBounding.max.x;
        break;
      case AxisIndex.Y:
        plane.normal.set(0, 1, 0);
        plane.d = -this._modelBounding.max.y;
        break;
      case AxisIndex.Z:
        plane.normal.set(0, 0, 1);
        plane.d = -this._modelBounding.max.z;
        break;
      case AxisIndex.FACE:
        normal = this._faceSelection?.getFaceEntity()?.getNormal();
        position = this._faceSelection?.getPosition();

        if (normal && position) {
          plane.setFromPointAndNormal(position, normal);
        } else {
          return null;
        }
        break;
      default:
        break;
    }

    return plane;
  }
}

export default CuttingPlaneController;
